// React
import { ReactElement, useCallback, useEffect, useState } from 'react';
// Next
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
// Design System
import { Flag } from '@utmb/design-system/flag/Flag';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Link } from '@utmb/design-system/link/link';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { Pagination } from '@utmb/design-system/pagination/Pagination';
import { Placeholder, Skeleton } from '@utmb/design-system/placeholder/Placeholder';
import { Image } from '@utmb/design-system/image/Image';
// Features
import AgeGroupFilter from '@utmb/features/world-series/index-ranking-table/filters/AgeGroupFilter';
import CategoryFilter from '@utmb/features/world-series/index-ranking-table/filters/CategoryFilter';
import GenderFilter from '@utmb/features/world-series/index-ranking-table/filters/GenderFilter';
import NationalityFilter from '@utmb/features/world-series/index-ranking-table/filters/NationalityFilter';
import MobileFilters from '@utmb/features/world-series/index-ranking-table/mobile-filters/MobileFilters';
// Contexts
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Types
import { IconsEnum } from '@utmb/types/icons';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { QueryProperties } from '@utmb/types/runner-ranking-table';
import { SexEnum } from '@utmb/types/enum/SexEnum';
import { Slice } from '@utmb/types/slice-handler';
import SearchRunnerResultView from '@utmb/types/view/SearchRunnerResultView';
import SliceEventsWorldIndexRankingTableView from '@utmb/types/view/SliceEventsWorldIndexRankingTableView';
// utils
import { fetchClientAPI } from '@utmb/utils/utmb';
import { getGender } from '@utmb/utils/gender';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import { v4 as uuidv4 } from 'uuid';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import debounce from 'lodash.debounce';
// Styles
import styles from './index-ranking-table.module.scss';
import { useCountry } from '@utmb/contexts/CountryContext';

const categories = {
    [PiCategoryEnum.GENERAL]: { text: 'General', headerClass: styles.header_general, cellClass: styles.cell_general },
    [PiCategoryEnum.PI20K]: { text: '20K', headerClass: styles.header_20k, cellClass: styles.cell_20k },
    [PiCategoryEnum.PI50K]: { text: '50K', headerClass: styles.header_50k, cellClass: styles.cell_50k },
    [PiCategoryEnum.PI100K]: { text: '100K', headerClass: styles.header_100k, cellClass: styles.cell_100k },
    [PiCategoryEnum.PI100M]: { text: '100M', headerClass: styles.header_100m, cellClass: styles.cell_100m },
};

export const IndexRankingTable = (props: Slice<SliceEventsWorldIndexRankingTableView>): ReactElement => {
    const { id, results, ageGroups } = props;

    const { countries, initCountries } = useCountry();
    const nationalities = countries;
    useEffect(() => {
        initCountries();
    }, []);

    const { limit } = results;

    const { locale } = useRouter();
    const { tenantId } = useTenancy();
    const { t } = useTranslation();

    const [currentPage, setCurrentPage] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [runnerResult, setRunnerResult] = useState<SearchRunnerResultView>(results);
    const [currentCategory, setCurrentCategory] = useState<PiCategoryEnum>(results.category);
    const [currentGender, setCurrentGender] = useState<SexEnum | string>('');
    const [currentAgeGroup, setCurrentAgeGroup] = useState<string>('');
    const [currentNationality, setCurrentNationality] = useState<string>('');

    const [isLoading, setIsLoading] = useState(false);

    const query: QueryProperties = {
        category: currentCategory,
        sex: currentGender,
        ageGroup: currentAgeGroup,
        nationality: currentNationality,
        limit,
        offset: 0,
        search: searchValue,
    };

    const { runners } = runnerResult;
    const totalPages = Math.ceil(runnerResult.nbHits / limit);

    const debouncedFetchData = useCallback(debounce(fetchData, 300), []);

    async function fetchData(query: Record<string, any>): Promise<void> {
        setIsLoading(true);

        if (query.offset === 0) {
            setCurrentPage(0);
        }

        const results = (await fetchClientAPI(tenantId, '/search/runners', query)) as SearchRunnerResultView;

        setRunnerResult(results);
        setIsLoading(false);
    }

    function handleInputChange(event: any): void {
        const search = event.target.value.trim();
        query.search = search;
        setSearchValue(search);
        debouncedFetchData(query);
    }

    function handlePaginationChange(event: any): void {
        setCurrentPage(event.selected);
        query.offset = event.selected * limit;
        fetchData(query);
    }

    return (
        <div id={id} className={`container g-0 ${styles.container}`}>
            <div className={`row g-0 ${styles.table_container}`}>
                <div className="col-12">
                    <div className={styles.search_container}>
                        <input onChange={handleInputChange} type="text" className={styles.input} placeholder={t('performanceTable.searchPlaceholder')} />
                        <Icon variant={IconsEnum.SEARCH} clss={styles.search_icon} />
                    </div>

                    <div className={`${styles.mobile_table_header} ${categories[currentCategory].headerClass}`}>
                        <div className={styles.mobile_filter_container}>
                            <CategoryFilter
                                categories={categories}
                                currentCategory={currentCategory}
                                fetchData={fetchData}
                                query={query}
                                setCurrentCategory={setCurrentCategory}
                            />
                        </div>
                        <MobileFilters
                            ageGroups={ageGroups}
                            bgColor={categories[currentCategory].headerClass}
                            currentAgeGroup={currentAgeGroup}
                            currentCategory={currentCategory}
                            currentGender={currentGender}
                            fetchData={fetchData}
                            query={query}
                            setCurrentAgeGroup={setCurrentAgeGroup}
                            setCurrentNationality={setCurrentNationality}
                            setCurrentGender={setCurrentGender}
                        />
                    </div>

                    <MyTable>
                        <MyTableHeader className={`${styles.desktop_table_header} ${categories[currentCategory].headerClass}`} height={65}>
                            <MyTableCell column={2} paddingHidden>
                                <CategoryFilter
                                    categories={categories}
                                    currentCategory={currentCategory}
                                    fetchData={fetchData}
                                    query={query}
                                    setCurrentCategory={setCurrentCategory}
                                />
                            </MyTableCell>
                            <MyTableCell column={4}>{t('performanceTable.name')}</MyTableCell>
                            <MyTableCell column={2} hiddenOnMobile>
                                <NationalityFilter
                                    nationalities={nationalities}
                                    currentNationality={currentNationality}
                                    fetchData={fetchData}
                                    query={query}
                                    setCurrentNationality={setCurrentNationality}
                                />
                            </MyTableCell>
                            <MyTableCell column={2} hiddenOnMobile>
                                <GenderFilter currentGender={currentGender} fetchData={fetchData} query={query} setCurrentGender={setCurrentGender} />
                            </MyTableCell>
                            <MyTableCell column={2} hiddenOnMobile paddingHidden>
                                <AgeGroupFilter
                                    ageGroups={ageGroups}
                                    currentAgeGroup={currentAgeGroup}
                                    currentCategory={currentCategory}
                                    fetchData={fetchData}
                                    query={query}
                                    setCurrentAgeGroup={setCurrentAgeGroup}
                                />
                            </MyTableCell>
                        </MyTableHeader>

                        {runners &&
                            runners.map((runner) => (
                                <MyTableRow key={uuidv4()} height={50}>
                                    <MyTableCell column={2}>
                                        {isLoading ? (
                                            <Skeleton width="25%" />
                                        ) : runner.ip ? (
                                            runner.ip > VALID_INDEX_THRESHOLD ? (
                                                runner.ip
                                            ) : (
                                                t('singleRunner.statistics.global.validIndex')
                                            )
                                        ) : (
                                            '-'
                                        )}
                                    </MyTableCell>
                                    <MyTableCell className={categories[currentCategory].cellClass} column={4}>
                                        <div className={styles.image_container}>
                                            {runner.picture ? (
                                                <Image
                                                    className={styles.image}
                                                    objectFit="cover"
                                                    layout="fill"
                                                    image={pIdToCloudinary(runner.picture, runner.fullname)}
                                                    width={30}
                                                    height={30}
                                                />
                                            ) : (
                                                <div className={styles.placeholder_container}>
                                                    <Placeholder className={styles.placeholder} width="30px" height="30px" logoWidth={20} logoHeight={20} />
                                                </div>
                                            )}
                                        </div>
                                        {isLoading ? (
                                            <Skeleton width="50%" />
                                        ) : (
                                            <Link href={`/${locale}/runner/${runner.uri}`} className="link-underline" prefetch={false}>
                                                {runner.fullname}
                                            </Link>
                                        )}
                                    </MyTableCell>
                                    <MyTableCell column={2} className={styles.cell_nationality} hiddenOnMobile label={t('performanceTable.nationality')}>
                                        {isLoading ? <Skeleton width="15%" height="28px" /> : <Flag isoCode={runner.nationality} />}
                                    </MyTableCell>
                                    <MyTableCell column={2} className={styles.cell_gender} hiddenOnMobile label="Gender">
                                        {isLoading ? <Skeleton width="20%" /> : t(getGender(runner.sex))}
                                    </MyTableCell>
                                    <MyTableCell column={2} className={styles.cell_age_group} hiddenOnMobile label={t('performanceTable.ageGroup')}>
                                        {isLoading ? <Skeleton width="20%" /> : runner.ageGroup}
                                    </MyTableCell>
                                </MyTableRow>
                            ))}
                        {!runners?.length && (
                            <Font.FuturaBook className={styles.no_result} desktop="20" mobile="16" as="div">
                                {t('performanceTable.noResult')}
                            </Font.FuturaBook>
                        )}
                    </MyTable>
                </div>
            </div>
            {totalPages > 1 && <Pagination forcePage={currentPage} PageCount={totalPages} onPageChange={handlePaginationChange} />}
        </div>
    );
};
